'use client';

import 'lib/sitecore/patchPagesPlaceholderHydration';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import { SvgIcon } from 'helpers/SvgIcon';
import useIsEditing from 'lib/hooks/useIsEditing';
import {
  AppPlaceholder,
  Placeholder,
  Page,
  type NextjsContentSdkComponent,
  type ComponentMap,
  useSitecore,
} from '@sitecore-content-sdk/nextjs';
import React, { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { initTrustpilotIfReady } from 'lib/trustpilot/widget';

// IMPORTANT: do NOT statically import `.sitecore/component-map` here.
//
// `.sitecore/component-map` imports every component in the project, many of
// which transitively import this `PlaceholderWrapper`. A static import would
// create a circular dependency:
//
//   component-map.ts
//      ↓ imports every component
//      → KeyContactWrapper, Hero, Container-*, Section, ...
//      ↓ those use placeholders
//      → PlaceholderWrapper.tsx
//      ↓ would import component-map  ← cycle
//
// Dev/Turbopack tolerates it; production webpack hoists the modules and
// throws `Cannot access 'w' before initialization` (minified binding for the
// not-yet-evaluated Default export of a component-map entry), which prevents
// the whole client tree from hydrating (e.g. the ExpertList on /our-people
// silently never renders).
//
// `SitecoreProvider` already places the component map on its context, and
// `useSitecore()` returns it at runtime (the public TS types omit it, hence
// the cast below). Reading from context gives us the same map without
// introducing a static module-graph edge back to component-map.

type PlaceholderProps = Parameters<typeof Placeholder>[0];

/**
 * AppPlaceholderComponent returns a bare component array when no `render` is
 * supplied. React 19 warns when those siblings lack keys. SDK 2.0 no longer
 * passes `key` via getChildComponentProps (unlike SDK 1.3 getRenderedComponentProps).
 */
function getElementClassName(element: React.ReactElement): string {
  const className = (element.props as { className?: unknown }).className;
  return typeof className === 'string' ? className : '';
}

/** Clone SDK empty-placeholder hosts so Pages' pre-hydrate mutations don't warn. Do not wrap. */
function withEmptyPlaceholderHydration(node: React.ReactNode): React.ReactNode {
  if (Array.isArray(node)) {
    return node.map((child) => withEmptyPlaceholderHydration(child));
  }
  if (!React.isValidElement(node)) {
    return node;
  }
  if (!getElementClassName(node).includes('sc-jss-empty-placeholder')) {
    return node;
  }
  return React.cloneElement(node as React.ReactElement<{ suppressHydrationWarning?: boolean }>, {
    suppressHydrationWarning: true,
  });
}

function withPlaceholderKeys(
  nodes: React.ReactNode[],
  placeholderName: string,
  renderings?: { uid?: string; itemId?: string }[],
  parentRendering?: { uid?: string; itemId?: string }
): React.ReactNode[] {
  return nodes.map((child, index) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const stableKey =
      renderings?.[index]?.uid ??
      renderings?.[index]?.itemId ??
      (index === 0 ? parentRendering?.uid ?? parentRendering?.itemId : undefined) ??
      `${placeholderName}-${index}`;

    if (child.key != null) {
      return child;
    }

    return React.cloneElement(child, { key: stableKey });
  });
}

export interface PlaceholderWrapperProps extends Omit<PlaceholderProps, 'page' | 'componentMap'> {
  page?: Page;
  componentMap?: ComponentMap<NextjsContentSdkComponent>;
  helpTextClassName?: string;
  helpTextHideIf?: boolean;
  helpTextContent?: React.ReactNode;
  /**
   * Recommended to use `render` instead and include the wrapping element
   * That way the editing help text can be rendered outside of the wrapping element instead of inside.
   * Most relevant for when using flex or grid displays
   */
  renderEach?: PlaceholderProps['renderEach'];
}

export function PlaceholderWrapper({
  helpTextClassName,
  helpTextHideIf,
  helpTextContent,
  render,
  renderEach,
  renderEmpty,
  name,
  page,
  componentMap,
  ...props
}: PlaceholderWrapperProps) {
  const isEditing = useIsEditing();
  const pathname = usePathname();
  const prevPathnameRef = useRef(pathname);
  // `useSitecore()`'s public type only exposes `{ page, api, setPage }`, but
  // at runtime `SitecoreProvider` also places `componentMap` on the same
  // context (see SitecoreProvider source). We read it from there to avoid a
  // static import of `.sitecore/component-map` and the circular dependency
  // it would create (see header comment). Type `page` as `Page` (not
  // optional) to match `useSitecore()`'s public typing — the previous code
  // relied on the same non-null shape.
  const { page: contextPage, componentMap: contextComponentMap } = useSitecore() as ReturnType<
    typeof useSitecore
  > & {
    componentMap?: ComponentMap<NextjsContentSdkComponent>;
  };

  // Trustpilot first-load init is owned by CodeEmbed Script onLoad.
  // On App Router navigations, bootstrap is already present — only then init new widgets.
  useEffect(() => {
    const pathChanged = prevPathnameRef.current !== pathname;
    prevPathnameRef.current = pathname;
    if (pathChanged) {
      initTrustpilotIfReady();
    }
  }, [pathname]);

  const resolvedPage = page ?? contextPage;
  const resolvedComponentMap = componentMap ?? contextComponentMap;
  const useAppPlaceholder = Boolean(resolvedPage && resolvedComponentMap);

  // Always pass `render` into AppPlaceholder so it never returns an unkeyed array.
  // Key siblings first, then apply caller `render` (and editing unwrap when needed).
  const keyedRender = useCallback<NonNullable<PlaceholderProps['render']>>(
    (components, data, phProps) => {
      const keyedComponents = withPlaceholderKeys(
        components as React.ReactNode[],
        name,
        data,
        phProps.rendering as { uid?: string; itemId?: string }
      ) as Parameters<NonNullable<PlaceholderProps['render']>>[0];

      if (!render) {
        return keyedComponents;
      }

      if (isEditing) {
        // When in editing mode, there is an extra layer that we need to get to the children of
        // to get the correct notes.  This seems to be a bug.
        const wrapperComponent = keyedComponents[0] as React.ReactElement<{
          children: React.ReactNode[];
        }>;
        const componentsToRender = wrapperComponent?.props?.children;
        const renderedChildren = render(componentsToRender, data, phProps) as React.ReactElement;

        wrapperComponent.props.children.splice(0, componentsToRender.length, renderedChildren);
        return wrapperComponent;
      }

      return render(keyedComponents, data, phProps);
    },
    [isEditing, render, name]
  );

  // Fix what seems to be a JSS bug.  If JSS fixes this in a later version, this can be removed
  const fixedRenderEach = useCallback<NonNullable<PlaceholderProps['renderEach']>>(
    (component, index) => {
      if (!renderEach) {
        return null;
      }
      if (isEditing) {
        // When in editing mode, there is an extra layer that we need to get to the children of
        // to get the correct notes.  This seems to be a bug.
        const wrapperComponent = component as React.ReactElement<{ children: React.ReactNode[] }>;
        const componentsToRender = wrapperComponent?.props?.children as React.ReactElement[];
        const renderedChildren = componentsToRender.map(
          (x, i) => renderEach(x, i) as React.ReactElement
        );

        wrapperComponent.props.children.splice(0, componentsToRender.length, [...renderedChildren]);
        return wrapperComponent;
      }

      return renderEach(component, index);
    },
    [isEditing, renderEach]
  );

  const renderEmptyKeyed = useCallback(
    (components: React.ReactNode[]) => {
      const keyed = withPlaceholderKeys(components, name, undefined, props.rendering as {
        uid?: string;
        itemId?: string;
      });
      const rendered = renderEmpty ? renderEmpty(keyed) : keyed;
      return withEmptyPlaceholderHydration(rendered);
    },
    [name, renderEmpty, props.rendering]
  );

  if (useAppPlaceholder) {
    if (!isEditing) {
      return (
        <AppPlaceholder
          {...props}
          page={resolvedPage}
          componentMap={resolvedComponentMap}
          name={name}
          render={keyedRender}
          renderEach={renderEach}
          renderEmpty={renderEmptyKeyed}
        />
      );
    }

    return (
      <div suppressHydrationWarning>
        <PlaceholderIndicator
          {...{ helpTextClassName, helpTextContent, helpTextHideIf, name }}
          direction="down"
        />
        <AppPlaceholder
          {...props}
          page={resolvedPage}
          componentMap={resolvedComponentMap}
          name={name}
          render={keyedRender}
          renderEach={renderEach ? fixedRenderEach : undefined}
          renderEmpty={renderEmptyKeyed}
        />
        <PlaceholderIndicator
          {...{ helpTextClassName, helpTextContent, helpTextHideIf, name }}
          direction="up"
        />
      </div>
    );
  }

  // If not edting, render it as-is.
  if (!isEditing) {
    return (
      <Placeholder
        {...props}
        name={name}
        render={keyedRender}
        renderEach={renderEach}
        // By default, if a render method is defined, JSS will use that even for empty renderings
        // Usually this isn't desired, so instead use the default implementation if one isn't specified
        renderEmpty={renderEmptyKeyed}
      />
    );
  }

  // Customize based on client needs
  return (
    <div suppressHydrationWarning>
      <PlaceholderIndicator
        {...{ helpTextClassName, helpTextContent, helpTextHideIf, name }}
        direction="down"
      />
      <Placeholder
        {...props}
        name={name}
        render={keyedRender}
        renderEach={renderEach ? fixedRenderEach : undefined}
        // By default, if a render method is defined, JSS will use that even for empty renderings
        // Usually this isn't desired, so instead use the default implementation if one isn't specified
        renderEmpty={renderEmptyKeyed}
      />
      <PlaceholderIndicator
        {...{ helpTextClassName, helpTextContent, helpTextHideIf, name }}
        direction="up"
      />
    </div>
  );
}

interface PlaceholderIndicatorrProps {
  helpTextClassName?: string;
  helpTextHideIf?: boolean;
  helpTextContent?: React.ReactNode;
  direction: 'up' | 'down';
  name: string;
}
function PlaceholderIndicator({
  helpTextClassName,
  helpTextHideIf,
  helpTextContent,
  direction,
  name,
}: PlaceholderIndicatorrProps) {
  const icon = direction === 'up' ? 'chevron-up' : 'chevron-down';

  return (
    <EditingHelpText className={helpTextClassName} hideIf={helpTextHideIf}>
      <div className="flex justify-center">
        <SvgIcon className="mx-2 my-1" icon={icon} size="xxs" />
        {helpTextContent ?? `Placeholder: ${name}`}
        <SvgIcon className="mx-2 my-1" icon={icon} size="xxs" />
      </div>
    </EditingHelpText>
  );
}
